import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { userAPI } from '../../API/userAPI';

// eslint-disable-next-line react/prop-types
const SetPasswordPage = ({ mode: initialMode }) => {
  const { t } = useTranslation();
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const isRedefinition = initialMode === 'redefinition' || window.location.pathname === '/reset-password';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loadingUser, setLoadingUser] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [isConflict, setIsConflict] = useState(false);

  const keyPrefix = isRedefinition ? 'users:resetPassword' : 'users:setPassword';

  // Password rules validation
  const hasMinLength = password.length >= 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasSpecialChar = /[^a-zA-Z0-9]/.test(password);
  const isValidPassword = hasMinLength && hasUppercase && hasSpecialChar;
  const mismatch = confirm.length > 0 && password !== confirm;

  const canSubmit = token && email && isValidPassword && password === confirm;

  useEffect(() => {
    if (!token) {
      setError(t(`${keyPrefix}.invalidToken`));
      setLoadingUser(false);
      return;
    }

    const fetchUser = isRedefinition
      ? userAPI.getResetUser(token)
      : userAPI.getInvitedUser(token);

    fetchUser
      .then(res => {
        if (res?.error || res?.status >= 400) {
          let msg = res?.description;
          if (!msg || msg === 'Erro desconhecido') {
            if (res?.status === 410) {
              msg = t(`${keyPrefix}.expiredToken`);
            } else if (res?.status === 409) {
              msg = isRedefinition
                ? t('users:resetPassword.pendingError')
                : t('users:setPassword.alreadySetError');
            } else {
              msg = t(`${keyPrefix}.invalidToken`);
            }
          }
          if (res?.status === 409) {
            setIsConflict(true);
          }
          setError(msg);
        } else if (res?.value?.email) {
          setEmail(res.value.email);
        } else {
          setError(t(`${keyPrefix}.invalidToken`));
        }
      })
      .catch(() => {
        setError(t(`${keyPrefix}.genericError`));
      })
      .finally(() => {
        setLoadingUser(false);
      });
  }, [token, isRedefinition, keyPrefix, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    setIsConflict(false);
    try {
      const res = isRedefinition
        ? await userAPI.resetPasswordFromToken(token, password)
        : await userAPI.setPassword(token, password);

      if (res?.error || res?.status >= 400) {
        let msg = res?.description;
        if (!msg || msg === 'Erro desconhecido') {
          if (res?.status === 410) {
            msg = t(`${keyPrefix}.expiredToken`);
          } else if (res?.status === 409) {
            msg = isRedefinition
              ? t('users:resetPassword.pendingError')
              : t('users:setPassword.alreadySetError');
          } else {
            msg = t(`${keyPrefix}.invalidToken`);
          }
        }
        if (res?.status === 409) {
          setIsConflict(true);
        }
        setError(msg);
      } else {
        setDone(true);
      }
    } catch {
      setError(t(`${keyPrefix}.genericError`));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f4f5f7',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 420,
          bgcolor: 'background.paper',
          borderRadius: '16px',
          boxShadow: '0 4px 24px rgba(16,24,40,0.10)',
          overflow: 'hidden',
        }}
      >
        {/* ── Header strip ── */}
        <Box
          sx={{
            bgcolor: 'primary.main',
            px: 4,
            py: 3.5,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#fff', letterSpacing: '-0.3px' }}>
            Gateway
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: 'rgba(255,255,255,0.75)', mt: 0.25 }}>
            Plataforma de Contagem de Pessoas
          </Typography>
        </Box>

        {/* ── Body ── */}
        <Box sx={{ px: 4, py: 4 }}>
          {loadingUser ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : done ? (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                {t(`${keyPrefix}.successTitle`)}
              </Typography>
              <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 3 }}>
                {t(`${keyPrefix}.successSubtitle`)}
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={() => { window.location.href = '/'; }}
                sx={{ textTransform: 'none', fontWeight: 600, py: 1.1 }}
              >
                {t('users:setPassword.goToLogin')}
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                {t(`${keyPrefix}.title`)}
              </Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 3 }}>
                {t(`${keyPrefix}.subtitle`)}
              </Typography>

              {error && (
                <Stack spacing={1.5} sx={{ mb: 2.5 }}>
                  <Alert severity={isConflict ? "info" : "error"} sx={{ fontSize: 13 }}>
                    {error}
                  </Alert>
                  {isConflict && (
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => {
                        const targetPath = isRedefinition ? '/set-password' : '/reset-password';
                        window.location.href = `${targetPath}?token=${token}`;
                      }}
                      sx={{ textTransform: 'none', fontWeight: 600, py: 0.8 }}
                    >
                      {isRedefinition
                        ? t('users:resetPassword.goToDefinition', 'Ir para definição da password')
                        : t('users:setPassword.goToRedefinition', 'Ir para redefinição da password')}
                    </Button>
                  )}
                </Stack>
              )}

              {email && (
                <TextField
                  label={t('users:setPassword.emailLabel')}
                  size="small"
                  fullWidth
                  disabled
                  value={email}
                  sx={{ mb: 2, '& .MuiInputBase-root': { fontSize: 13.5 } }}
                />
              )}

              <Stack spacing={2}>
                <TextField
                  label={t('users:setPassword.newPassword')}
                  type={showPwd ? 'text' : 'password'}
                  size="small"
                  fullWidth
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPwd((v) => !v)} edge="end">
                          {showPwd ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiInputBase-root': { fontSize: 13.5 } }}
                />

                {/* Password strength visual guidelines */}
                {password.length > 0 && (
                  <Box sx={{ px: 0.5, py: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography
                      variant="caption"
                      color={hasMinLength ? 'success.main' : 'error.main'}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 500 }}
                    >
                      {hasMinLength ? '✓' : '•'} {t('users:setPassword.helperMinLength')}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={hasUppercase ? 'success.main' : 'error.main'}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 500 }}
                    >
                      {hasUppercase ? '✓' : '•'} {t('users:setPassword.helperUppercase')}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={hasSpecialChar ? 'success.main' : 'error.main'}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 500 }}
                    >
                      {hasSpecialChar ? '✓' : '•'} {t('users:setPassword.helperSpecial')}
                    </Typography>
                  </Box>
                )}

                <TextField
                  label={t('users:setPassword.confirmPassword')}
                  type={showPwd ? 'text' : 'password'}
                  size="small"
                  fullWidth
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  error={mismatch}
                  helperText={mismatch ? t('users:setPassword.mismatchError') : ' '}
                  disabled={submitting}
                  sx={{ '& .MuiInputBase-root': { fontSize: 13.5 } }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={!canSubmit || submitting}
                  sx={{ textTransform: 'none', fontWeight: 600, py: 1.1 }}
                >
                  {submitting ? t('users:setPassword.saving') : t(`${keyPrefix}.submit`)}
                </Button>
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SetPasswordPage;
